import { useState, useEffect, useMemo, useRef } from 'react'
import { useApi } from '@/hooks/useApi'

interface ProxyConnectionConfig {
  host?: string | null
  port?: number | null
  username?: string | null
  password?: string | null
  raw_config?: string | null
}

interface ProxyNode {
  id: number
  proxy_type: string
  connection_config: ProxyConnectionConfig
  country_code: string | null
  is_active: boolean
  last_checked_at: string | null
  created_at: string | null
}

interface ProxyStats {
  total: number
  active: number
  inactive: number
  by_country: Record<string, number>
  by_type: Record<string, number>
}

type SortField = 'proxy_type' | 'country_code' | 'created_at' | 'last_checked_at'
type SortDir = 'asc' | 'desc'

export default function ProxiesPage() {
  const { api } = useApi()
  const [proxies, setProxies] = useState<ProxyNode[]>([])
  const [error, setError] = useState<string | null>(null)
  const [scraperRunning, setScraperRunning] = useState(false)
  const [scraperProgress, setScraperProgress] = useState<{
    status: string
    progress: number
    total: number
    current_source?: string
    current_url?: string
    proxies_added?: number
    estimated_total?: number
    urls_processed?: number
    source_added?: number
    resolved?: number
    not_resolved?: number
    error?: string
  } | null>(null)
  const [scraperTaskId, setScraperTaskId] = useState<string | null>(null)
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const [checkerRunning, setCheckerRunning] = useState(false)
  const [geoResolveRunning, setGeoResolveRunning] = useState(false)
  const [deduplicateRunning, setDeduplicateRunning] = useState(false)
  // deduplicateTaskId is used internally but not displayed
  // deduplicateProgress is used internally but not displayed
  const deduplicateInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  // geoResolveTaskId is used internally but not displayed
  const geoResolveInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const [geoResolveProgress, setGeoResolveProgress] = useState<{
    resolved: number
    failed: number
    status: string
  } | null>(null)
  const [addFormOpen, setAddFormOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [activeOnly, setActiveOnly] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalProxies, setTotalProxies] = useState(0)
  const [filteredTotal, setFilteredTotal] = useState(0)
  const pageSize = 50

  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterCountry, setFilterCountry] = useState('')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const [newType, setNewType] = useState('http')
  const [newHost, setNewHost] = useState('')
  const [newPort, setNewPort] = useState('')
  const [newCountry, setNewCountry] = useState('XX')
  const [newUsername, setNewUsername] = useState('')
  const [newPassword, setNewPassword] = useState('')

  const [proxyStats, setProxyStats] = useState<ProxyStats | null>(null)

  const fetchStats = async () => {
    try {
      const res = await api.get('/proxies/stats')
      setProxyStats(res.data)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }

  const uniqueTypes = useMemo(() => {
    if (proxyStats?.by_type) {
      return Object.keys(proxyStats.by_type).sort()
    }
    return [...new Set(proxies.map(p => p.proxy_type))].sort()
  }, [proxyStats, proxies])

  const uniqueCountries = useMemo(() => {
    if (proxyStats?.by_country) {
      return Object.keys(proxyStats.by_country).sort()
    }
    return [...new Set(proxies.map(p => p.country_code || ''))].filter((c): c is string => Boolean(c)).sort()
  }, [proxyStats, proxies])

  const displayProxies = useMemo(() => {
    let result = [...proxies]
    result.sort((a, b) => {
      let aVal: string | number | Date = ''
      let bVal: string | number | Date = ''

      switch (sortField) {
        case 'proxy_type':
          aVal = a.proxy_type ?? ''
          bVal = b.proxy_type ?? ''
          break
        case 'country_code':
          aVal = a.country_code ?? ''
          bVal = b.country_code ?? ''
          break
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0
          break
        case 'last_checked_at':
          aVal = a.last_checked_at ? new Date(a.last_checked_at).getTime() : 0
          bVal = b.last_checked_at ? new Date(b.last_checked_at).getTime() : 0
          break
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortDir === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal)
    })

    return result
  }, [proxies, searchQuery, filterType, filterCountry, sortField, sortDir])

  const filteredCount = filteredTotal

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setFilterType('')
    setFilterCountry('')
    setCurrentPage(1)
  }

  const hasActiveFilters = searchQuery.trim() !== '' || filterType !== '' || filterCountry !== ''

  const fetchProxies = async () => {
    try {
      const offset = (currentPage - 1) * pageSize
      const params: Record<string, any> = {
        active_only: activeOnly ? 1 : 0,
        limit: pageSize,
        offset: offset,
      }
      // Add search filter if provided
      if (searchQuery.trim()) {
        params.search = searchQuery.trim()
      }
      // Add type filter if selected
      if (filterType) {
        params.proxy_type = filterType
      }
      // Add country filter if selected
      if (filterCountry) {
        params.country = filterCountry
      }
      const res = await api.get('/proxies/nodes', { params })

      let data = res.data
      let items: ProxyNode[] = []
      let total = 0

      if (Array.isArray(data)) {
        items = data
        total = data.length
      } else if (data && typeof data === 'object') {
        items = Array.isArray(data.items) ? data.items : []
        total = data.total ?? data.items?.length ?? 0
      }

      setProxies(items)
      setTotalProxies(total)
      setFilteredTotal(total)
      setError(null)
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message)
      setProxies([])
    }
  }

  useEffect(() => { fetchProxies() }, [activeOnly, currentPage, searchQuery, filterType, filterCountry])
  useEffect(() => { fetchStats() }, [])
  
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current)
      }
    }
  }, [])

  useEffect(() => {
    const savedTaskId = localStorage.getItem('scraper_task_id')
    if (savedTaskId) {
      setScraperTaskId(savedTaskId)
      setScraperRunning(true)
      setScraperProgress({ status: 'running', progress: 0, total: 0 })
      pollScraperProgress(savedTaskId)
      progressInterval.current = setInterval(() => pollScraperProgress(savedTaskId), 2000)
    }
  }, [])

  const pollScraperProgress = async (taskId: string) => {
    try {
      const res = await api.get(`/proxies/scraper/status/${taskId}`)
      const data = res.data
      
      if (data.status === 'FAILURE' || data.status === 'REVOKED') {
        const errorMsg = data.result?.get?.('failure_reason') || data.result || 'Task failed'
        setScraperProgress({ 
          status: 'error', 
          progress: 0, 
          total: 0,
          error: typeof errorMsg === 'string' ? errorMsg : String(errorMsg)
        })
        setScraperTaskId(null)
        localStorage.removeItem('scraper_task_id')
        if (progressInterval.current) {
          clearInterval(progressInterval.current)
          progressInterval.current = null
        }
        setScraperRunning(false)
        return
      }
      
      if (data.status === 'SUCCESS') {
        const result = data.result
        const resolveStats = result?.result?.result
        setScraperProgress({
          status: 'completed',
          progress: data.progress?.progress || 0,
          total: data.progress?.total || 0,
          resolved: resolveStats?.resolved || 0,
          not_resolved: resolveStats?.not_resolved || 0,
          proxies_added: data.progress?.proxies_added || 0,
        })
        setScraperTaskId(null)
        localStorage.removeItem('scraper_task_id')
        if (progressInterval.current) {
          clearInterval(progressInterval.current)
          progressInterval.current = null
        }
        setScraperRunning(false)
        fetchStats()
        setTimeout(() => fetchProxies(), 1000)
        return
      }
      
      if (data.progress) {
        setScraperProgress(data.progress)
      }
      
      // Poll timeout check
      const currentProgress = data.progress
      if (currentProgress && currentProgress.progress >= currentProgress.total && currentProgress.total > 0) {
        setScraperProgress({
          status: 'error',
          progress: 0,
          total: 0,
          error: 'Task timed out. Please check backend logs.'
        })
        setScraperTaskId(null)
        localStorage.removeItem('scraper_task_id')
        if (progressInterval.current) {
          clearInterval(progressInterval.current)
          progressInterval.current = null
        }
        setScraperRunning(false)
      }
    } catch (err) {
      console.error('Failed to poll progress:', err)
    }
  }

  const handleScrape = async () => {
    setScraperRunning(true)
    setError(null)
    setScraperProgress({ status: 'starting', progress: 0, total: 0 })
    try {
      const res = await api.post('/proxies/scraper/run')
      const taskId = res.data.task_id
      setScraperTaskId(taskId)
      localStorage.setItem('scraper_task_id', taskId)
      pollScraperProgress(taskId)
      progressInterval.current = setInterval(() => pollScraperProgress(taskId), 2000)
    } catch (err: unknown) {
      let msg = 'Failed to run scraper'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } }
        const status = axiosErr.response?.status
        const detail = axiosErr.response?.data?.detail
        if (status === 401) msg = 'Not authenticated'
        else if (status === 403) msg = 'Access denied'
        else if (detail) msg = detail
      } else if (err instanceof Error) {
        msg = err.message
      }
      setError(msg)
      setScraperRunning(false)
    }
  }

  const handleCancelScraper = async () => {
    if (!scraperTaskId) return
    try {
      await api.post(`/proxies/scraper/cancel/${scraperTaskId}`)
      setScraperProgress(prev => prev ? { ...prev, status: 'cancelled' } : null)
      setScraperTaskId(null)
      localStorage.removeItem('scraper_task_id')
      if (progressInterval.current) {
        clearInterval(progressInterval.current)
        progressInterval.current = null
      }
      setScraperRunning(false)
    } catch (err) {
      console.error('Cancel failed:', err)
    }
  }

  const handleCheck = async () => {
    setCheckerRunning(true)
    setError(null)
    try {
      await api.post('/proxies/checker/run')
      setTimeout(() => setCheckerRunning(false), 5000)
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message)
      setCheckerRunning(false)
    }
  }

  const handleDeduplicate = async () => {
    if (!confirm('Remove duplicate proxies? This will keep only the oldest record for each host:port:type combination.')) return
    setDeduplicateRunning(true)
    setError(null)
    try {
      const res = await api.post('/proxies/deduplicate')
      const taskId = res.data.task_id
      // setDeduplicateTaskId(taskId)
      // Start polling for progress
      const interval = setInterval(async () => {
        try {
          const statusRes = await api.get(`/proxies/scraper/status/${taskId}`)
          if (statusRes.data.status === 'completed' || statusRes.data.status === 'failed') {
            clearInterval(interval)
            setDeduplicateRunning(false)
            // Clear progress (not displayed in UI)
            // setDeduplicateTaskId(null)
            if (statusRes.data.status === 'completed') {
              setProxyStats(null)
              fetchStats()
              fetchProxies()
              alert('Removed ' + statusRes.data.result.deleted + ' duplicate proxies')
            } else {
              setError('Deduplicate failed: ' + (statusRes.data.error || 'Unknown error'))
            }
          } else {
            // Progress tracking internally (not displayed in UI)
          }
        } catch (err) {
          clearInterval(interval)
          setDeduplicateRunning(false)
          // Clear progress (not displayed in UI)
          setError('Failed to check deduplicate status')
        }
      }, 2000)
      // Store interval for cleanup
      deduplicateInterval.current = interval
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message)
      setDeduplicateRunning(false)
    }
  }

  const handleGeoResolve = async () => {
    setGeoResolveRunning(true)
    setGeoResolveProgress({ resolved: 0, failed: 0, status: 'running' })
    setError(null)
    try {
      const res = await api.post('/proxies/geo/resolve')
      const taskId = res.data.task_id
      // setGeoResolveTaskId(taskId)
      // Start polling for progress
      const interval = setInterval(async () => {
        try {
          const statusRes = await api.get(`/proxies/scraper/status/${taskId}`)
          if (statusRes.data.status === 'completed' || statusRes.data.status === 'failed') {
            clearInterval(interval)
            setGeoResolveRunning(false)
            // setGeoResolveTaskId(null)
            if (statusRes.data.status === 'completed') {
              setGeoResolveProgress({
                resolved: statusRes.data.result.resolved || 0,
                failed: statusRes.data.result.failed || 0,
                status: 'completed'
              })
              fetchStats()
              fetchProxies()
            } else {
              setError('Geo resolve failed: ' + (statusRes.data.error || 'Unknown error'))
            }
          } else {
            setGeoResolveProgress({
              resolved: 0,
              failed: 0,
              status: statusRes.data.status
            })
          }
        } catch (err) {
          clearInterval(interval)
          setGeoResolveRunning(false)
          setGeoResolveProgress({ resolved: 0, failed: 0, status: 'error' })
          setError('Failed to check geo resolve status')
        }
      }, 2000)
      // Store interval for cleanup
      geoResolveInterval.current = interval
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message)
      setGeoResolveRunning(false)
    }
  }

  const handleAdd = async () => {
    if (!newHost || !newPort) {
      setError('Host and port are required')
      return
    }
    setAdding(true)
    setError(null)
    try {
      await api.post('/proxies/batch-add', {
        proxies: [{
          proxy_type: newType,
          host: newHost,
          port: parseInt(newPort),
          country_code: newCountry,
          username: newUsername || undefined,
          password: newPassword || undefined,
        }],
      })
      setAddFormOpen(false)
      setNewHost('')
      setNewPort('')
      setNewCountry('XX')
      setNewUsername('')
      setNewPassword('')
      fetchProxies()
      fetchStats()
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message)
    } finally {
      setAdding(false)
    }
  }


  const activeCount = proxies.filter(p => p.is_active).length

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Proxy Management</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={e => { setActiveOnly(e.target.checked); setCurrentPage(1) }}
              className="rounded border-gray-300"
            />
            Active only
          </label>
          {scraperRunning ? (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${scraperProgress ? Math.round((scraperProgress.progress / (scraperProgress.total || 1)) * 100) : 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-600">{scraperProgress ? Math.round((scraperProgress.progress / (scraperProgress.total || 1)) * 100) : 0}%</span>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                {scraperProgress?.status === 'resolving_countries' ? 'Resolving...' : 'Running...'}
              </button>
              <button
                onClick={handleCancelScraper}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleScrape}
              disabled={scraperRunning}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              Run Scraper
            </button>
          )}
          <button
            onClick={handleCheck}
            disabled={checkerRunning}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {checkerRunning ? 'Checking...' : 'Check'}
          </button>
          <button
            onClick={handleDeduplicate}
            disabled={deduplicateRunning}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
          >
            {deduplicateRunning ? 'Removing...' : 'Remove Duplicates'}
          </button>
          <button
            onClick={handleGeoResolve}
            disabled={geoResolveRunning}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
          >
            {geoResolveRunning ? 'Resolving...' : 'Fix Countries'}
          </button>
          <button
            onClick={() => setAddFormOpen(!addFormOpen)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            {addFormOpen ? 'Cancel' : '+ Add Proxy'}
          </button>
        </div>
      </div>

      {geoResolveProgress && geoResolveProgress.status === 'completed' && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <span className="text-green-800 text-sm">
            ✅ Country resolution complete: {geoResolveProgress.resolved} resolved, {geoResolveProgress.failed} failed
          </span>
        </div>
      )}

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{totalProxies}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Inactive</p>
          <p className="text-2xl font-bold text-red-600">{proxies.length - activeCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Filtered</p>
          <p className="text-2xl font-bold text-blue-600">{filteredCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1) }}
              placeholder="Search type, host, country..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={e => { setFilterType(e.target.value); setCurrentPage(1) }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="">All Types</option>
              {uniqueTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <select
              value={filterCountry}
              onChange={e => { setFilterCountry(e.target.value); setCurrentPage(1) }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            >
              <option value="">All Countries</option>
              {uniqueCountries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                ✕ Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {scraperRunning && scraperProgress && (
        <div className={`mb-4 p-4 border rounded-lg ${scraperProgress.error ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {scraperProgress.error ? (
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
              ) : (
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              )}
              <span className="font-medium">
                {scraperProgress.error ? (
                  <span className="text-red-800">Error: {scraperProgress.error}</span>
                ) : scraperProgress.status === 'resolving_countries' 
                  ? 'Resolving country codes...' 
                  : scraperProgress.status === 'completed'
                  ? 'Completed!'
                  : 'Scraping proxies...'}
              </span>
            </div>
            <span className="text-sm font-mono">
              {scraperProgress.error ? 'Failed' : `${scraperProgress.progress}/${scraperProgress.total} URLs`}
            </span>
          </div>
          
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
            <div 
              className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: `${scraperProgress.total > 0 ? Math.round((scraperProgress.progress / scraperProgress.total) * 100) : 0}%` }}
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {scraperProgress.current_source && (
              <div>
                <span className="text-gray-500">Current Source:</span>
                <p className="font-medium text-gray-900">{scraperProgress.current_source}</p>
              </div>
            )}
            {scraperProgress.estimated_total !== undefined && (
              <div>
                <span className="text-gray-500">Estimated Proxies:</span>
                <p className="font-medium text-gray-900">{scraperProgress.estimated_total.toLocaleString()}</p>
              </div>
            )}
            {scraperProgress.proxies_added !== undefined && (
              <div>
                <span className="text-gray-500">Added to DB:</span>
                <p className="font-medium text-green-600">{scraperProgress.proxies_added.toLocaleString()}</p>
              </div>
            )}
            {scraperProgress.urls_processed !== undefined && (
              <div>
                <span className="text-gray-500">URLs Processed:</span>
                <p className="font-medium text-gray-900">{scraperProgress.urls_processed}</p>
              </div>
            )}
            {scraperProgress.resolved !== undefined && scraperProgress.resolved > 0 && (
              <div>
                <span className="text-gray-500">Country Resolved:</span>
                <p className="font-medium text-green-600">{scraperProgress.resolved}</p>
              </div>
            )}
            {scraperProgress.not_resolved !== undefined && scraperProgress.not_resolved > 0 && (
              <div>
                <span className="text-gray-500">Country Failed:</span>
                <p className="font-medium text-red-600">{scraperProgress.not_resolved}</p>
              </div>
            )}
          </div>
          
          {scraperProgress.current_url && (
            <p className="mt-2 text-xs text-blue-500 truncate" title={scraperProgress.current_url}>
              URL: {scraperProgress.current_url}
            </p>
          )}
          
          {scraperProgress.source_added !== undefined && scraperProgress.source_added > 0 && (
            <p className="mt-1 text-xs text-green-600">
              +{scraperProgress.source_added} proxies added from current source
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {addFormOpen && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Proxy</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={newType}
                onChange={e => setNewType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              >
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
                <option value="socks4">SOCKS4</option>
                <option value="socks5">SOCKS5</option>
                <option value="v2ray">V2Ray</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Host / IP</label>
              <input
                type="text"
                value={newHost}
                onChange={e => setNewHost(e.target.value)}
                placeholder="1.2.3.4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
              <input
                type="number"
                value={newPort}
                onChange={e => setNewPort(e.target.value)}
                placeholder="8080"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                value={newCountry}
                onChange={e => setNewCountry(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              >
                <option value="XX">XX (Unknown)</option>
                {uniqueCountries.filter(c => c !== 'XX').map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username (optional)</label>
              <input
                type="text"
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                placeholder="auth user"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password (optional)</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="auth password"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
          </div>
          <button
            onClick={handleAdd}
            disabled={adding || !newHost || !newPort}
            className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
          >
            {adding ? 'Adding...' : 'Add Proxy'}
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('proxy_type')}
              >
                Type {sortField === 'proxy_type' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Host / IP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Port</th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('country_code')}
              >
                Country {sortField === 'country_code' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('last_checked_at')}
              >
                Last Checked {sortField === 'last_checked_at' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('created_at')}
              >
                Created {sortField === 'created_at' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {displayProxies.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  {hasActiveFilters ? 'No proxies match your filters' : 'No proxies found'}
                </td>
              </tr>
            ) : (
              displayProxies.map(proxy => {
                const host = proxy.connection_config?.host || '—'
                const port = proxy.connection_config?.port ?? '—'
                return (
                  <tr key={proxy.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">{proxy.proxy_type}</td>
                    <td className="px-6 py-4 text-sm font-mono text-blue-600">{host}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{port}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{proxy.country_code || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        proxy.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {proxy.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {proxy.last_checked_at ? new Date(proxy.last_checked_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {proxy.created_at ? new Date(proxy.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={async () => {
                          if (!confirm('Delete this proxy?')) return
                          try {
                            await api.delete(`/proxies/${proxy.id}`)
                            fetchProxies()
                            fetchStats()
                          } catch (err) {
                            if (err instanceof Error) setError(err.message)
                          }
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalProxies)} of {totalProxies} proxies
          {hasActiveFilters && <span className="text-blue-600 ml-2">(filtered: {filteredCount} results)</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">Page {currentPage}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalProxies / pageSize), p + 1))}
            disabled={currentPage >= Math.ceil(totalProxies / pageSize)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
